import { Col, Form, Row, Stack } from 'react-bootstrap'
import { IntegrationServiceId } from '../../../../../../../common/integrations'
import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { useCallback, useEffect, useMemo } from 'react'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { useGetDescribeQuery } from '../../../../../../../store/integrations/integrations'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { FormFieldDefinition } from '../../../hooks'

export interface PredefinedSessionAdminSettingsProps {
  serviceId: IntegrationServiceId
  disabled?: boolean
}
const crmEnableSaveToHousehold: IntegrationServiceId[] = [
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.ENGAGE,
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
]

const crmEnableCustomFieldsList: IntegrationServiceId[] = [IntegrationServiceId.SALESFORCE, IntegrationServiceId.PRACTIFI, IntegrationServiceId.XLR8]

const PREDEFINED_CUSTOMIZABLE_FIELDS = [{ zocksKey: 'household', label: 'Household', required: false }]

export const PredefinedSessionAdminSettings: React.FC<PredefinedSessionAdminSettingsProps> = ({ serviceId, disabled }) => {
  const {
    watch,
    setValue,
    formState: { errors },
    clearErrors,
    trigger,
  } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)

  const contactFields = watch('mappings.contact.fields')
  const currentObjectName = watch('mappings.contact.objectName')
  const saveToHouseholdEnabled = (watch('mappings.session.additionalInformation.saveToHousehold') as boolean) || false

  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: currentObjectName! },
    },
    { skip: !currentObjectName || !accountId || disabled },
  )

  const currentObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])

  const saveToHouseholdChange = useCallback(
    async (enabled: boolean) => {
      setValue('mappings.session.additionalInformation.saveToHousehold', enabled, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue],
  )

  useEffect(() => {
    const validateHouseholdField = async () => {
      if (saveToHouseholdEnabled) {
        await trigger('mappings.contact.fields.household')
      } else {
        clearErrors('mappings.contact.fields.household')
      }
    }

    validateHouseholdField()
  }, [saveToHouseholdEnabled, trigger, clearErrors])

  const fieldOptions: SelectOption[] = useMemo(
    () =>
      currentObjectFormFieldDefinitions.map((field: { name: string; label: string }): SelectOption => {
        return {
          id: field.name,
          label: `${field.label} (${field.name})`,
        }
      }) || [],
    [currentObjectFormFieldDefinitions],
  )

  const handleFieldChange = useCallback(
    (zocksKey: string, externalField: string) => {
      const newDescriptors = {
        type: 'field' as const,
        value: externalField,
      }

      setValue(`mappings.contact.fields.${zocksKey}`, [newDescriptors], {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue],
  )

  return (
    <>
      {crmEnableSaveToHousehold.includes(serviceId) && (
        <Row className='mb-2 mt-2'>
          <Col>
            <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
              <div>Export meeting to household</div>
            </Stack>
          </Col>
          <Col md='auto'>
            <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
              <Form.Switch
                id='export-meeting-household-switch'
                checked={saveToHouseholdEnabled}
                onChange={(e) => saveToHouseholdChange(e.currentTarget.checked)}
                disabled={disabled}
              />
            </Stack>
          </Col>
        </Row>
      )}

      {crmEnableCustomFieldsList.includes(serviceId) &&
        PREDEFINED_CUSTOMIZABLE_FIELDS.map((mapping) => {
          const storedValue = contactFields?.[mapping.zocksKey]?.[0]?.value

          const currentOption =
            fieldOptions.find((opt: SelectOption) => opt.id === storedValue) || (storedValue ? { id: storedValue, label: storedValue } : null)

          const fieldError = errors.mappings?.contact?.fields?.[mapping.zocksKey]
          const label = mapping.label // No need to replace space unless you really want to

          return (
            <Row key={mapping.zocksKey} className='mb-2 mt-2 align-items-center'>
              <Col>
                <div className='mb-1'>
                  <span className='zocks-text-gray-600'>{label}</span>
                  {mapping.required && <span className='text-danger ms-1'>*</span>}
                </div>

                <SelectDropdown
                  searchable
                  options={fieldOptions}
                  onOptionSelected={(selected) => handleFieldChange(mapping.zocksKey, selected.id)}
                  placeholder='Select field'
                  initialSelectedOption={currentOption}
                  loading={isDescribeFetching}
                  disabled={disabled}
                />

                <div className='d-block' style={{ minHeight: '20px' }}>
                  {fieldError && <div className='invalid-feedback d-block'>{fieldError.message || 'This field is required'}</div>}
                </div>
              </Col>
            </Row>
          )
        })}
    </>
  )
}
